import React, { useMemo } from "react";

interface TimeSliderProps {
  /** ISO string of the start of available range */
  rangeStart: string;
  /** ISO string of the end of available range */
  rangeEnd: string;
  /** Currently selected hour ISO string (or null for latest) */
  selectedHour: string | null;
  onChange: (hour: string | null) => void;
}

function formatHour(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}

const TimeSlider: React.FC<TimeSliderProps> = ({
  rangeStart,
  rangeEnd,
  selectedHour,
  onChange,
}) => {
  const startMs = new Date(rangeStart).getTime();
  const endMs = new Date(rangeEnd).getTime();

  // Limit to last 24h for slider range
  const sliderStartMs = useMemo(
    () => Math.max(startMs, endMs - 24 * 60 * 60 * 1000),
    [startMs, endMs]
  );
  const totalHours = Math.max(1, Math.floor((endMs - sliderStartMs) / (60 * 60 * 1000)));

  const currentVal = selectedHour
    ? Math.round((new Date(selectedHour).getTime() - sliderStartMs) / (60 * 60 * 1000))
    : totalHours;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = parseInt(e.target.value, 10);
    if (val >= totalHours) {
      onChange(null); // latest
    } else {
      const ms = sliderStartMs + val * 60 * 60 * 1000;
      onChange(new Date(ms).toISOString());
    }
  };

  return (
    <div className="flex items-center gap-3 px-4 py-2 bg-surface-800/90 backdrop-blur-sm rounded-lg border border-border/50 w-full max-w-2xl">
      <span className="text-caption text-text-muted whitespace-nowrap">
        {formatHour(new Date(sliderStartMs).toISOString())}
      </span>
      <input
        type="range"
        min={0}
        max={totalHours}
        value={currentVal}
        onChange={handleChange}
        className="flex-1 h-1.5 accent-quantum-500 cursor-pointer"
      />
      <span className="text-caption text-text-muted whitespace-nowrap">
        {selectedHour ? formatHour(selectedHour) : "NOW"}
      </span>
    </div>
  );
};

export default TimeSlider;
